import React from 'react'
import { LuCalendar1, LuMinus, LuPlus, LuRotateCw } from 'react-icons/lu'

import CalendarBasedPicker from '@/components/integration/nylas/scheduler/components/calendarDayPicker/CalendarBasedPicker'
import SchedulerSection from '@/components/integration/nylas/scheduler/components/SchedulerSection'
import SimpleDayPicker from '@/components/integration/nylas/scheduler/components/simpleDayPicker/SimpleDayPicker'
import { useSchedulerConfig } from '@/components/integration/nylas/scheduler/context/SchedulerConfigContext'
import { useSchedulerSelection } from '@/components/integration/nylas/scheduler/context/SchedulerSelectionContext'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/heading'
import { Spinner } from '@/components/ui/spinner'

interface Props {
  isLoadingAvailability: boolean
  isFetchingAvailability: boolean
  isAvailabilityFetchError: boolean
  onRetryAvailabilityFetch: () => void
}

const MeetingDatePicker: React.FC<Props> = ({
  isLoadingAvailability,
  isFetchingAvailability,
  isAvailabilityFetchError,
  onRetryAvailabilityFetch,
}) => {
  const { durationMinutes, availableLocations, availableHomeVisitLocations } =
    useSchedulerConfig()
  const {
    showFullCalendar,
    availabilityFilter,
    switchToCalendar,
    switchToSimple,
  } = useSchedulerSelection()

  // Named in the Date heading when the times shown are one office's advisers'
  const availabilityOfficeName = [
    ...(availableLocations ?? []),
    ...(availableHomeVisitLocations ?? []),
  ].find((location) => location.id === availabilityFilter.locationId)?.name

  return (
    <SchedulerSection className="mb-6">
      <Heading size={4} className="mb-4 flex items-center gap-2">
        <LuCalendar1 size={16} />
        {[
          'Date',
          availabilityOfficeName && `${availabilityOfficeName} advisers`,
          durationMinutes && `${durationMinutes} min meetings`,
        ]
          .filter(Boolean)
          .join(' · ')}
        {isLoadingAvailability && <Spinner />}
        {!isAvailabilityFetchError && (
          <div className="flex flex-1 justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="-me-2 text-xs"
              onClick={() => {
                if (showFullCalendar) {
                  switchToSimple()
                } else {
                  switchToCalendar()
                }
              }}
            >
              {showFullCalendar ? (
                <>
                  <LuMinus />
                  Show fewer dates
                </>
              ) : (
                <>
                  <LuPlus />
                  Show more dates
                </>
              )}
            </Button>
          </div>
        )}
      </Heading>

      {isAvailabilityFetchError ? (
        <Alert variant="warning">
          <AlertTitle>Failed to load availability</AlertTitle>

          <AlertDescription className="flex flex-col gap-3">
            <p>
              Something went wrong with fetching the available meeting times.
            </p>

            <div>
              <Button
                variant="primary"
                size="sm"
                onClick={onRetryAvailabilityFetch}
                disabled={isFetchingAvailability}
              >
                <LuRotateCw />
                {isFetchingAvailability ? 'Retrying...' : 'Retry'}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : showFullCalendar ? (
        <CalendarBasedPicker />
      ) : (
        <SimpleDayPicker />
      )}
    </SchedulerSection>
  )
}

export default MeetingDatePicker
